/* eslint-disable @repo/internal/react/require-jsdoc */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { jsx } from '@compiled/react';

import { Status } from '@af/design-system-docs-ui';
import { SideNav, SideNavBody } from '@atlaskit/navigation-system';
import {
	ExpandableMenuItem,
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { Divider, MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';

import { type NavigationItem, type NavigationItemData } from './types';
import { checkCurrentPath } from './utils/check-current-path';
import { checkCurrentPathAncestor } from './utils/check-current-path-ancestor';

export const SideNavigation: ({
	id,
	navigation,
	currentPath,
	onExpand,
	onCollapse,
}: {
	id?: string;
	navigation: NavigationItem[];
	currentPath: string;
	onExpand?: () => void;
	onCollapse?: () => void;
}) => JSX.Element = ({
	id,
	navigation,
	currentPath,
	onExpand,
	onCollapse,
}: {
	id?: string;
	navigation: NavigationItem[];
	currentPath: string;
	onExpand?: () => void;
	onCollapse?: () => void;
}) => {
	return (
		<SideNav id={id} defaultWidth={350} onExpand={onExpand} onCollapse={onCollapse}>
			<SideNavBody>
				<RecursiveMenuItems navigation={navigation} currentPath={currentPath} />
			</SideNavBody>
		</SideNav>
	);
};

const RecursiveMenuItems = ({
	navigation,
	currentPath,
}: {
	navigation: NavigationItem[];
	currentPath: string;
}) => {
	return (
		<React.Fragment>
			{navigation.map(({ item, subitems }: NavigationItem) => {
				// Base case. Leaf node doesn't have any nested routes
				if (subitems.length === 0) {
					return (
						<Fragment key={item.to || item.title}>
							{item.to === '/design-at-atlassian' && <Divider />}
							<LinkMenuItem
								elemAfter={item.status && <Status type={item.status.type} />}
								href={item.to || '/'}
								isSelected={checkCurrentPath(item.to, currentPath, subitems)}
							>
								<span aria-label={item.status && `${item.title} - ${item.status.type}`}>
									{item.title}
								</span>
							</LinkMenuItem>
						</Fragment>
					);
				}

				// Recursive case 1
				// If it doesn't have a `href`, then it's a component grouping
				// (like 'Primitives' or 'Layout and Structure')
				// So we can render it as a heading and group
				if (!item.to) {
					return (
						<MenuSection key={item.to || item.title}>
							<MenuSectionHeading key={item.title}>{item.title}</MenuSectionHeading>
							<MenuList>
								<RecursiveMenuItems navigation={subitems} currentPath={currentPath} />
							</MenuList>
						</MenuSection>
					);
				}

				// Recursive case 2
				// Else, render it as a collapsible menu item
				return (
					<ControlledExpandableMenuItem
						key={item.to || item.title}
						item={item}
						subitems={subitems}
						currentPath={currentPath}
					/>
				);
			})}
		</React.Fragment>
	);
};

/**
 * Controlled state version of the ExpandableMenuItem from nav4.
 * We need to use controlled state so we can expand a parent menu item when a child is selected, based on the
 * current path.
 */
const ControlledExpandableMenuItem = ({
	item,
	subitems,
	currentPath,
}: {
	item: NavigationItemData;
	subitems: NavigationItem[];
	currentPath: string;
}) => {
	const [isExpanded, setIsExpanded] = useState(() => {
		if (!item.to) {
			return false;
		}

		return checkCurrentPathAncestor(item.to, currentPath);
	});

	const isSelected = checkCurrentPath(item.to, currentPath, subitems);

	/**
	 * Expand the parent menu item (the current item) when a child is selected.
	 * Using a layout effect to ensure the parent has expanded before the child menu item is scrolled into view.
	 */
	useLayoutEffect(() => {
		if (!item.to) {
			return;
		}

		if (isSelected) {
			// Exit early if the current item (expandable parent) is selected
			return;
		}

		// When a child becomes selected (e.g. the path changes), the parent should expand
		const isChildSelected = checkCurrentPathAncestor(item.to, currentPath);
		if (!isChildSelected) {
			return;
		}

		setIsExpanded(true);
	}, [currentPath, isSelected, item.to]);

	/**
	 * Tracks whether the pending expansion toggle came from the trigger link rather than the
	 * chevron. `ExpandableMenuItemTrigger` only fires `onClick` for the link, and always before
	 * `onExpansionToggle`, so this is set by the time the toggle handler runs.
	 */
	const isTriggerLinkClick = useRef(false);

	const handleTriggerClick = useCallback(() => {
		isTriggerLinkClick.current = true;
	}, []);

	const handleExpansionToggle = useCallback(
		(nextIsExpanded: boolean) => {
			// Clicking the trigger link from somewhere else navigates the reader into this section,
			// so collapsing it would hide the page they are about to land on. From there the click
			// only ever opens.
			//
			// Once they are already on the parent's own page there is nothing to hide, so the link
			// behaves as a plain toggle and can close the section again.
			if (isTriggerLinkClick.current) {
				isTriggerLinkClick.current = false;

				if (!isSelected) {
					setIsExpanded(true);
					return;
				}
			}

			setIsExpanded(nextIsExpanded);
		},
		[isSelected],
	);

	return (
		<ExpandableMenuItem
			isExpanded={isExpanded}
			key={item.to}
			onExpansionToggle={handleExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				elemAfter={item.status && <Status type={item.status.type} />}
				href={item.to}
				isSelected={isSelected}
				onClick={handleTriggerClick}
			>
				<span aria-label={item.status && `${item.title} - ${item.status.type}`}>{item.title}</span>
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<MenuSection>
					<RecursiveMenuItems navigation={subitems} currentPath={currentPath} />
				</MenuSection>
			</ExpandableMenuItemContent>
		</ExpandableMenuItem>
	);
};
